<template>
  <section style="width: 1200px">
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card" style="width: 1200px">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ title }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group
              horizontal
              name="status"
              attribute="attributes.status"
              :label="$t('attributes.status')"
            >
              <b-switch v-model="form.status" name="status"></b-switch>
            </form-group>

            <form-group horizontal name="sort" :label="$t('attributes.sort')">
              <b-select
                v-model="form.sort"
                name="sort"
                :placeholder="
                  $t('message.please_select') + $t('attributes.sort')
                "
                expanded
              >
                <option
                  v-for="option in sortOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="area_code"
              attribute="attributes.area"
              :label="$t('attributes.area')"
            >
              <b-select
                v-model="selectedArea"
                name="area_code"
                :loading="loadingAreas"
                :placeholder="
                  $t('message.please_select') + $t('attributes.area')
                "
                expanded
                @input="onSelectArea"
              >
                <option value="" disabled>
                  {{ $t('message.please_select') + $t('attributes.area') }}
                </option>
                <option
                  v-for="option in areas"
                  :key="option.code"
                  :value="option"
                >
                  {{ option.name }}
                </option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="subarea"
              attribute="attributes.offer_subarea"
              :label="$t('attributes.offer_subarea')"
            >
              <b-input
                v-model="form.subarea"
                name="subarea"
                :placeholder="
                  $t('message.please_input') + $t('attributes.offer_subarea')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group horizontal name="topic" :label="$t('attributes.topic')">
              <b-input
                v-model="form.topic"
                name="topic"
                :placeholder="
                  $t('message.please_input') + $t('attributes.topic')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="create_date"
              attribute="attributes.offer_dates"
              :label="$t('attributes.offer_dates')"
            >
              <b-datepicker
                :placeholder="
                  $t('message.please_select') + $t('attributes.offer_dates')
                "
                v-model="selectedDates"
                range
                @input="onSelectedDates"
              >
              </b-datepicker>
            </form-group>

            <form-group
              horizontal
              name="content"
              attribute="attributes.content"
              :label="$t('attributes.content')"
            >
              <div class="content ck-content">
                <editor
                  ref="content_editor"
                  v-model="form.content"
                  image-type="News"
                ></editor>
              </div>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="sending"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ isUpdate ? $t('attributes.update') : $t('attributes.add') }}
          </b-button>
        </footer>

        <b-loading
          :active.sync="isLoading"
          :is-full-page="false"
          :can-cancel="false"
        ></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators'
import { AREA_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import Editor from '@/components/editors/Editor4'

export default {
  name: 'OfferForm',
  components: { Editor },

  props: {
    offer: {
      type: Object,
      required: true
    }
  },
  data() {
    const isUpdate = !!this.offer.id
    const title =
      (isUpdate ? this.$t('attributes.modify') : this.$t('attributes.add')) +
      this.$t('attributes.offer_area_type')

    const today = this.$dayjs()
    const startDate = this.offer.start_date
      ? this.$dayjs(this.offer.start_date).toDate()
      : today.toDate()
    const endDate = this.offer.end_date
      ? this.$dayjs(this.offer.end_date).toDate()
      : today.toDate()

    return {
      isUpdate: isUpdate,
      title: title,

      sortOptions: [...Array(10).keys()].map(i => i + 1),

      selectedDates: [startDate, endDate],

      loadingAreas: false,
      areas: [],
      selectedArea: null,

      sending: false,
      form: this.extractFormValues(this.offer)
    }
  },
  validations: {
    form: {
      status: { required },
      sort: { required },
      type: { required },
      area_code: { required },
      subarea: { required },
      topic: { required, maxLength: maxLength(100) },
      content: { required },
      start_date: { required },
      end_date: { required }
    }
  },
  computed: {
    isLoading() {
      return this.loadingAreas || this.sending
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        this.loadingAreas = true
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.form.area_code) {
          this.selectedArea = this.areas.find(
            a => a.code === this.form.area_code
          )
        }
      } catch (e) {
        this.areas = []
        this.selectedArea = null

        notifyService.fetch_error('area', e)
      } finally {
        this.loadingAreas = false
      }
    },
    extractFormValues(data) {
      const today = this.$dayjs()
      const startDate = data.start_date
        ? this.$dayjs(data.start_date).toDate()
        : today.toDate()
      const endDate = data.end_date
        ? this.$dayjs(data.end_date).toDate()
        : today.toDate()

      this.selectedDates = [startDate, endDate]

      return {
        id: data.id,
        status: data.status,
        sort: data.sort,
        type: 'C',
        area_code: data.area_code,
        subarea: data.subarea,
        topic: data.topic,
        content: data.content,
        start_date: this.formatDate(startDate),
        end_date: this.formatDate(endDate)
      }
    },
    onSelectArea(option) {
      this.form.area_code = option && option.code
    },
    onSelectedDates() {
      this.form.start_date = this.formatDate(this.selectedDates[0])
      this.form.end_date = this.formatDate(this.selectedDates[1])
    },
    async onSend() {
      this.form.content = this.$refs.content_editor.getData()

      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
